import {
    buildRetentionTrendsAllUsers,
    buildRetentionTrendsNewUsers,
    parseRetentionTrendsResults,
} from '@pendo/middleware';
import { runAggregationOnce } from '@/utils/aggregation';
import { http } from '@pendo/http';
import { i18n_t as $t } from '@/utils/i18n';
import get from 'lodash/get';
import moment from '@utils/moment-wrapper';
import momentTimeZone from 'moment-timezone';

const RETENTION_KINDS = ['visitor', 'account'];

function buildUrlWithParams (baseUrl, options = {}) {
    const allowedParams = ['revision', 'force'];
    const params = new URLSearchParams();

    allowedParams.forEach((param) => {
        if (options[param] !== undefined && options[param] !== null) {
            params.set(param, options[param]);
        }
    });

    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
}

export function getRetentionInsights (requestBody, options = {}) {
    const url = buildUrlWithParams('/api/streaming/s/_SID_/insight/generate', options);

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http.post(url, requestBody, requestOptions).then((res) => res.data);
}

export function getInitialState () {
    return {
        insights: null,
        insightRequestBody: null,
        isLoading: false,
        error: null,
    };
}

function getFirstAvailableAppId (appList) {
    if (!appList || appList.length === 0) {
        return null;
    }

    const firstItem = appList[0];
    if (firstItem?.apps && firstItem.apps.length > 0) {
        return firstItem.apps[0]?.id || null;
    }

    return firstItem?.id || null;
}

export function determineAppId (rootGetters, reportIndex = 0) {
    const trendsDataSettings = rootGetters['userSettings/settingByName']('trends-data-settings', true);

    if (trendsDataSettings) {
        const { appId } = trendsDataSettings;
        if (appId) {
            return appId;
        }
    }

    const retentionHistory = rootGetters['savedReports/retentionHistory'] || [];

    if (retentionHistory.length > 0 && reportIndex < retentionHistory.length) {
        const mostRecentReportId = retentionHistory[reportIndex].id;
        const mostRecentReport = rootGetters['savedReports/reportById']?.(mostRecentReportId);
        if (mostRecentReport?.appId === 'all') {
            reportIndex++;

            return determineAppId(rootGetters, reportIndex);
        }

        if (mostRecentReport?.appId) {
            const { appId } = mostRecentReport;
            if (typeof appId === 'number' || typeof appId === 'string') {
                return appId;
            }
        }
    }

    const availableApps = rootGetters['apps/analyticsAppsListWithIcons'];

    if (availableApps?.length > 0) {
        return getFirstAvailableAppId(availableApps);
    }

    return null;
}

function getCurrentWeekStart (rootGetters) {
    const subscription = rootGetters?.['subscription/getActiveSub'];
    const zone = get(subscription, 'timeZone');

    return zone
        ? momentTimeZone.tz(zone).startOf('isoWeek')
        : moment(undefined, { ignoreTimeZone: true }).startOf('isoWeek');
}

function computeStartTimestamp (rootGetters) {
    return getCurrentWeekStart(rootGetters).clone().subtract(7, 'weeks').valueOf();
}

export function buildRequestBody (appId, includeAnonymous = false, rootGetters = null) {
    const currentWeekStart = getCurrentWeekStart(rootGetters);

    const periodStart = currentWeekStart.clone().subtract(7, 'weeks').valueOf();
    const periodEnd = currentWeekStart.clone().add(1, 'week').valueOf();

    const timeRangeStart = currentWeekStart.clone().subtract(3, 'weeks').startOf('week').format('MMMM D');
    const timeRangeEnd = currentWeekStart.clone().subtract(2, 'weeks').endOf('week').format('MMMM D');

    return {
        job_spec: {
            app_id: appId,
            metrics: [
                { metric_type: 'week_over_week', kind: 'visitor' },
                { metric_type: 'week_over_week', kind: 'account' },
            ],
            trend_type: 'retention',
            include_anonymous: includeAnonymous,
            start_timestamp: periodStart,
            end_timestamp: periodEnd,
        },
        timeRangeStart,
        timeRangeEnd,
    };
}

export function buildMwSettings (appId, includeAnonymous = false, rootGetters = null) {
    return {
        appId,
        hasShowAnonymousFlag: !!includeAnonymous,
        startTimestamp: computeStartTimestamp(rootGetters),
    };
}

function wrapAsAggConnect (pipelineSpec) {
    return {
        name: pipelineSpec.name,
        productArea: pipelineSpec.productArea,
        subProductArea: pipelineSpec.subProductArea,
        responseFormat: 'rows',
        build: () => pipelineSpec.pipeline,
    };
}

export function extractRows (aggResult) {
    if (Array.isArray(aggResult)) return aggResult;
    if (aggResult && Array.isArray(aggResult.rows)) return aggResult.rows;
    if (aggResult && Array.isArray(aggResult.messages)) {
        const rows = [];
        for (const message of aggResult.messages) {
            if (message && Array.isArray(message.rows)) {
                rows.push(...message.rows);
            }
        }

        return rows;
    }

    return [];
}

export async function executeRetentionPipelines (settings) {
    const newUsersByKind = Object.fromEntries(
        RETENTION_KINDS.map((kind) => [kind, wrapAsAggConnect(buildRetentionTrendsNewUsers(settings, kind))]),
    );
    const allUsersByKind = Object.fromEntries(
        RETENTION_KINDS.map((kind) => [kind, wrapAsAggConnect(buildRetentionTrendsAllUsers(settings, kind))]),
    );

    const runFor = (aggsByKind) =>
        Promise.all(
            RETENTION_KINDS.map(async (kind) => [kind, extractRows(await runAggregationOnce(aggsByKind[kind]))]),
        ).then(Object.fromEntries);

    const [newUsers, allUsers] = await Promise.all([runFor(newUsersByKind), runFor(allUsersByKind)]);

    return { newUsers, allUsers };
}

export function wrapMwResultsAsArtifact (appId, executed) {
    const inputs = RETENTION_KINDS.map((kind) => ({
        kind,
        newUsersRows: executed.newUsers[kind],
        allUsersRows: executed.allUsers[kind],
    }));

    const artifact = parseRetentionTrendsResults({ appId, inputs });

    return { artifacts: { retention_insights: artifact } };
}

export function buildAppDataFromRetentionState (state, rootGetters, fallbackAppId = null) {
    const retentionInsightsArtifact = state?.artifacts?.retention_insights;
    const responseAppId = retentionInsightsArtifact?.data?.app_id || fallbackAppId;

    const app = rootGetters['apps/getAppById']?.(responseAppId);
    const appIcon = rootGetters['apps/getIconByAppId']?.(responseAppId);
    const appData = {
        name: app?.displayName || app?.name,
        icon: appIcon,
        appId: responseAppId,
    };

    return appData;
}

function parseSummary (summary, _tagName) {
    if (!summary || typeof summary !== 'string') {
        return { textBefore: summary || '', textAfter: '', showAppTag: false };
    }

    const tagNamePlaceholder = '{{APP_NAME}}';
    const placeholderIndex = summary.indexOf(tagNamePlaceholder);

    if (placeholderIndex !== -1) {
        const textBefore = summary.substring(0, placeholderIndex).trim();
        const textAfter = summary.substring(placeholderIndex + tagNamePlaceholder.length).trim();

        return { textBefore, textAfter, showAppTag: true };
    }

    return { textBefore: summary, textAfter: '', showAppTag: false };
}

export function transformInsightsResponse (finalState, appData) {
    const retentionInsightsArtifact = finalState?.artifacts?.retention_insights;
    const retentionInsightsData = retentionInsightsArtifact?.data;
    if (!retentionInsightsData?.results || !Array.isArray(retentionInsightsData.results)) {
        return [];
    }

    const tagName = appData?.name;
    const rawIcon = appData?.icon;
    const appIcon = typeof rawIcon === 'string' ? { type: rawIcon, size: 12 } : { ...rawIcon, size: 12 };

    return retentionInsightsData.results
        .map((result) => {
            const { metric, kind } = result;

            if (metric !== 'week_over_week') return null;

            const title = kind === 'account' ? $t('trends.Account retention') : $t('trends.Visitor retention');

            const {
                current_retention: newUsersPercentage,
                previous_retention: existingUsersPercentage,
                delta,
                current_week_label: currentPeriodLabel,
                previous_week_label: previousPeriodLabel,
            } = result;
            const { textBefore, textAfter, showAppTag } = parseSummary(result.summary, tagName);

            return {
                title,
                newUsersPercentage,
                existingUsersPercentage,
                delta,
                currentPeriodLabel,
                previousPeriodLabel,
                textBeforeTagName: textBefore,
                textAfterTagName: textAfter,
                showAppTag,
                tagName,
                appIcon,
                kind,
                appId: appData?.appId,
            };
        })
        .filter(Boolean);
}
